import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Layout";
import {
  Check,
  Compass,
  ShieldCheck,
  HandHeart,
  Sparkles,
  Scissors,
  Wrench,
  Wallet,
  Droplets,
  LifeBuoy,
  Leaf,
} from "lucide-react";
import { setSeo } from "@/utils/seo";
import { trackEvent as track } from "@/utils/analytics";

const PATHS = [
  {
    icon: Compass,
    title: "Sto pensando di iniziare",
    text: "Per chi sta valutando una protesi capillare e vuole orientarsi prima di scegliere.",
    cta: "Sto valutando",
    param: "iniziare",
    testid: "path-new",
  },
  {
    icon: Check,
    title: "Uso già una protesi",
    text: "Per chi utilizza già una protesi e vuole affrontare dubbi o difficoltà legati all'esperienza quotidiana.",
    cta: "La uso già",
    param: "utente",
    testid: "path-current",
  },
];

const AREAS = [
  { icon: Sparkles, title: "Cura e manutenzione", text: "Come pulire, far durare e tenere in ordine il tuo sistema giorno dopo giorno." },
  { icon: Compass, title: "Scelta della protesi", text: "Basi, densità, materiali e caratteristiche per orientarti tra le soluzioni." },
  { icon: Wrench, title: "Tecniche e strumenti", text: "Adesivi, nastri, solventi e strumenti giusti per applicazioni più semplici." },
  { icon: Scissors, title: "Taglio e personalizzazione", text: "Come adattare, rifinire e personalizzare il sistema al tuo viso." },
  { icon: Wallet, title: "Costi e durata", text: "Cosa aspettarsi in termini di spesa, durata e sostituzioni nel tempo." },
  { icon: Droplets, title: "Sport e sudorazione", text: "Gestire attività fisica, sudore e caldo mantenendo tenuta e comfort." },
  { icon: LifeBuoy, title: "Problemi frequenti", text: "Distacchi, prurito, dubbi quotidiani: come affrontarli con calma." },
];

const BASES = [
  { img: "/base-poly.jpg", title: "Poly", text: "Superficie liscia in poliuretano, nodi sigillati e resa compatta." },
  { img: "/base-skin.jpg", title: "Skin", text: "Membrana ultrasottile e trasparente, per un effetto molto leggero." },
  { img: "/base-lace.jpg", title: "Lace", text: "Rete traspirante annodata a mano, leggera e ariosa sulla pelle." },
  { img: "/base-hybrid.jpg", title: "Basi ibride", text: "Zone lace e poly combinate per unire naturalezza e tenuta." },
];

const SYSTEM_TYPES_LIST = [
  "Sistemi parziali",
  "Sistemi completi",
  "Toupet",
  "Hair system",
  "Infoltimenti non chirurgici",
  "Soluzioni maschili e femminili",
];

const ASPECTS = [
  "Naturalezza",
  "Durata",
  "Traspirabilità",
  "Adesione",
  "Manutenzione",
  "Sport e sudorazione",
  "Stile e personalizzazione",
];

const PHOTOS = [
  { img: "/photo-trucchi.jpg", caption: "Basi e materiali, viste da vicino" },
  { img: "/photo-scelta.jpg", caption: "Confronto tra basi: lace e skin" },
  { img: "/photo-tecniche.jpg", caption: "Applicazione e strumenti del mestiere" },
  { img: "/photo-taglio.jpg", caption: "Taglio e personalizzazione" },
  { img: "/photo-tempi.jpg", caption: "Manutenzione e lavaggio del sistema" },
  { img: "/photo-costi.jpg", caption: "Costi, durata e pianificazione" },
];

const TRUST = [
  { icon: HandHeart, title: "Esperienza pratica", text: "Consigli costruiti sulla pratica reale, non soltanto sulla teoria." },
  { icon: Compass, title: "Orientamento indipendente", text: "Informazioni chiare, senza spingerti verso un prodotto specifico." },
  { icon: ShieldCheck, title: "Riservatezza e rispetto", text: "Ogni dubbio merita ascolto, con la massima discrezione." },
];

const ASK_POINTS = [
  "Nessun obbligo",
  "Nessun abbonamento",
  "Risposta privata",
  "Nessuna vendita di protesi",
  "Nessuna diagnosi medica",
];

const btnPrimary =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-sage px-7 py-3.5 font-manrope text-[15px] font-semibold text-ivory shadow-[0_10px_24px_rgba(63,90,82,0.20)] transition-all duration-300 hover:bg-[#33463f] active:scale-[0.98]";
const btnSecondary =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-sage/60 bg-transparent px-7 py-3.5 font-manrope text-[15px] font-semibold text-sage transition-all duration-300 hover:border-sage hover:bg-sage/5 active:scale-[0.98]";

export default function Home() {
  const videoRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  useEffect(() => {
    setSeo(
      "HairHelp | Orientamento indipendente sui sistemi capillari",
      "Orientamento indipendente, esperienza pratica e strumenti concreti per comprendere sistemi capillari, basi, materiali, manutenzione e scelte. HairHelp non vende protesi: ti aiuta a orientarti."
    );
  }, []);

  const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-ivory font-manrope text-charcoal" data-testid="home-page">
      {/* HEADER */}
      <Navbar />
      <div className="h-8 bg-gradient-to-b from-black to-transparent md:h-10" data-testid="header-fade" />

      {/* HERO */}
      <section className="px-5 pt-14 md:px-8 md:pt-20" data-testid="hero-section">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-beige px-4 py-1.5 font-manrope text-[13px] font-semibold tracking-normal text-sage">
            <Leaf className="h-3.5 w-3.5 shrink-0 text-terracotta" strokeWidth={2} /> Consigli pratici per uomini e donne che usano o stanno pensando di iniziare a usare una protesi capillare
          </span>
          <h1 className="mt-7 font-editorial text-[2.35rem] font-semibold leading-[1.12] tracking-tight text-charcoal sm:text-5xl lg:text-[3.25rem] lg:leading-[1.1]" data-testid="hero-title">
            Consigli pratici e sinceri per vivere meglio con la <span className="text-sage">protesi capillare</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl font-manrope text-[17px] leading-relaxed text-charcoal/80 md:text-lg" data-testid="hero-subtitle">
            Ti aiutiamo a capire materiali, manutenzione, costi e differenze tra le diverse protesi, con informazioni
            semplici e basate sull&rsquo;esperienza.
          </p>
          <p className="mx-auto mt-4 max-w-xl font-manrope text-sm font-medium text-charcoal/60">
            Ti aiutiamo a evitare scelte costose e a capire quale sistema è compatibile con esigenze, manutenzione e budget.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-testid="hero-actions">
            <Link to="/fai-una-domanda" data-testid="hero-primary-cta" onClick={() => track("cta_consiglio_gratuito", { location: "home_hero" })} className={btnPrimary}>
              Chiedi un consiglio gratuito
            </Link>
            <button type="button" onClick={() => scrollToId("posizionamento")} data-testid="hero-secondary-cta" className={btnSecondary}>
              Scopri HairHelp
            </button>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="px-5 pt-12 md:px-8 md:pt-16" data-testid="video-section">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-3xl border border-line shadow-[0_20px_50px_rgba(37,37,37,0.12)]">
            <video
              ref={videoRef}
              src="/hairhelp-intro.mp4"
              poster="/hairhelp-poster.jpg"
              className="aspect-video w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              data-testid="home-video"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/75 via-charcoal/10 to-transparent p-5 md:p-7">
              <p className="font-editorial text-base font-medium text-ivory md:text-lg">
                Esperienza reale, non teoria. Persone che conoscono davvero il settore.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POSIZIONAMENTO */}
      <section id="posizionamento" className="mt-16 bg-beige px-5 py-16 md:mt-24 md:px-8 md:py-24" data-testid="positioning-section">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-charcoal md:text-[2.5rem]" data-testid="positioning-title">
            Capire prima di scegliere.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
            Non vendiamo una soluzione: ti aiutiamo a capire quale strada valutare. Mettiamo ordine tra informazioni,
            materiali, tecniche e dubbi quotidiani, così puoi orientarti prima di scegliere.
          </p>
        </div>
      </section>

      {/* DUE PERCORSI */}
      <section className="px-5 pt-16 md:px-8 md:pt-24" data-testid="paths-section">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-5 md:grid-cols-2 md:gap-6">
            {PATHS.map((p) => (
              <div key={p.testid} data-testid={p.testid} className="flex flex-col rounded-3xl border border-line bg-beige p-8 md:p-10">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ivory text-sage">
                  <p.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h2 className="mt-5 font-editorial text-2xl font-semibold text-charcoal md:text-[1.75rem]">{p.title}</h2>
                <p className="mt-3 flex-1 font-manrope text-base leading-relaxed text-charcoal/75">{p.text}</p>
                <Link to={`/fai-una-domanda?percorso=${p.param}`} onClick={() => track("path_select", { path: p.param })} data-testid={`${p.testid}-cta`} className={btnPrimary + " mt-7 w-full sm:w-fit"}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AREE DI COMPETENZA */}
      <section className="mt-16 bg-beige px-5 py-16 md:mt-24 md:px-8 md:py-24" data-testid="areas-section">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-charcoal md:text-[2.5rem]" data-testid="areas-title">
              Le aree in cui possiamo aiutarti
            </h2>
            <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/75 md:text-lg">
              Argomenti concreti, nati dai dubbi più comuni di chi vive con una protesi capillare.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AREAS.map((a) => (
              <div key={a.title} data-testid={`area-${a.title}`} className="rounded-2xl border border-line bg-ivory p-6 transition-colors duration-300 hover:border-sage/50">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-beige text-sage">
                  <a.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 font-editorial text-lg font-semibold text-charcoal">{a.title}</h3>
                <p className="mt-2 font-manrope text-[15px] leading-relaxed text-charcoal/70">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SISTEMI, BASI E MATERIALI */}
      <section className="px-5 pt-16 md:px-8 md:pt-24" data-testid="systems-section">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-charcoal md:text-[2.5rem]" data-testid="systems-title">
              Conosciamo sistemi diversi, perché ogni persona ha esigenze diverse.
            </h2>
            <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
              Non esiste una soluzione uguale per tutti. Ti aiutiamo a comprendere caratteristiche, vantaggi, limiti
              e manutenzione dei diversi sistemi capillari, senza indirizzarti verso un prodotto specifico.
            </p>
          </div>

          {/* Basi e materiali */}
          <div className="mt-12">
            <h3 className="font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Basi e materiali</h3>
            <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-5">
              {BASES.map((b) => (
                <figure key={b.title} data-testid={`base-${b.title}`} className="rounded-2xl border border-line bg-beige p-3 shadow-sm transition-shadow duration-300 hover:shadow-md">
                  <div className="overflow-hidden rounded-xl border border-line bg-ivory">
                    <img src={b.img} alt={`Illustrazione tecnica della base ${b.title}`} className="aspect-square w-full object-cover" loading="lazy" />
                  </div>
                  <figcaption className="px-1 pt-3">
                    <p className="font-editorial text-lg font-semibold text-charcoal">{b.title}</p>
                    <p className="mt-1 font-manrope text-[13px] leading-relaxed text-charcoal/65">{b.text}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-beige px-4 py-2 font-manrope text-sm font-medium text-charcoal/75" data-testid="base-membrane-chip">
                <span className="h-1.5 w-1.5 rounded-full bg-terracotta" /> Membrane capillari
              </span>
            </div>
          </div>

          {/* Tipologie di sistema */}
          <div className="mt-10">
            <h3 className="font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Tipologie di sistema</h3>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {SYSTEM_TYPES_LIST.map((t) => (
                <span key={t} data-testid={`type-${t}`} className="inline-flex items-center gap-2 rounded-full border border-line bg-beige px-4 py-2 font-manrope text-sm font-medium text-charcoal/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-sage" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Aspetti da valutare */}
          <div className="mt-10">
            <h3 className="font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Aspetti da valutare</h3>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {ASPECTS.map((a) => (
                <span key={a} data-testid={`aspect-${a}`} className="inline-flex items-center gap-2 rounded-full border border-line bg-beige px-4 py-2 font-manrope text-sm font-medium text-charcoal/80">
                  <span className="h-1.5 w-1.5 rounded-full bg-sage" /> {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOTOGRAFIE CONCRETE */}
      <section className="mt-16 bg-beige px-5 py-16 md:mt-24 md:px-8 md:py-24" data-testid="photos-section">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-charcoal md:text-[2.5rem]" data-testid="photos-title">
              Esperienza pratica, non solo teoria
            </h2>
            <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/75 md:text-lg">
              Materiali, strumenti e attività reali del lavoro quotidiano con i sistemi capillari.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5">
            {PHOTOS.map((p) => (
              <figure key={p.img} data-testid={`photo-${p.caption}`} className="overflow-hidden rounded-2xl border border-line bg-ivory">
                <img src={p.img} alt={p.caption} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                <figcaption className="px-4 py-3 font-manrope text-[13px] font-medium leading-snug text-charcoal/75">{p.caption}</figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-5 font-manrope text-[11px] text-charcoal/45">Immagini illustrative del lavoro con i sistemi capillari.</p>
        </div>
      </section>

      {/* SEZIONE FIDUCIA */}
      <section className="px-5 pt-16 md:px-8 md:pt-24" data-testid="trust-section">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-charcoal md:text-[2.5rem]" data-testid="trust-title">
            Un aiuto concreto, prima di qualsiasi acquisto
          </h2>
          <p className="mx-auto mt-5 max-w-3xl font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
            Chiariamo i dubbi su materiali, tenuta, manutenzione e costi, così eviti errori prima di decidere.
          </p>
          <div className="mt-12 grid gap-8 sm:grid-cols-3 sm:gap-6">
            {TRUST.map((t) => (
              <div key={t.title} className="flex flex-col items-center text-center" data-testid={`trust-${t.title}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-line bg-beige text-terracotta">
                  <t.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 font-editorial text-xl font-semibold text-charcoal">{t.title}</h3>
                <p className="mt-2 max-w-xs font-manrope text-[15px] leading-relaxed text-charcoal/70">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHIEDICI UN CONSIGLIO */}
      <section className="px-5 pt-16 md:px-8 md:pt-24" data-testid="ask-section">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl bg-charcoal px-7 py-12 text-center md:px-16 md:py-16">
          <h2 className="font-editorial text-[2rem] font-semibold leading-tight text-ivory md:text-[2.5rem]" data-testid="ask-title">
            Hai bisogno di un consiglio? Siamo qui per aiutarti.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl font-manrope text-base leading-relaxed text-ivory/85 md:text-lg">
            Hai una domanda sulla tua protesi, sulla manutenzione o sui primi passi? Scrivici: riceverai un parere
            gratuito, privato e concreto, rivolto a uomini e donne.
          </p>
          <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6">
            {ASK_POINTS.map((pt) => (
              <span key={pt} className="inline-flex items-center gap-2 font-manrope text-sm font-medium text-ivory/90">
                <Check className="h-4 w-4 shrink-0 text-sage-light" strokeWidth={2.5} /> {pt}
              </span>
            ))}
          </div>
          <Link to="/fai-una-domanda" data-testid="ask-cta" onClick={() => track("cta_consiglio_gratuito", { location: "home_ask" })} className={btnPrimary + " mt-9"}>
            Raccontaci il tuo dubbio in modo riservato
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mt-20 bg-charcoal px-5 py-14 md:px-8" data-testid="home-footer">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 md:grid-cols-4">
            <div className="md:col-span-2">
              <img src="/logo-masthead.png" alt="HairHelp" className="h-16 w-auto rounded-2xl" />
              <p className="mt-4 max-w-sm font-manrope text-sm leading-relaxed text-ivory/55">
                Orientamento indipendente e strumenti pratici per chi utilizza una protesi capillare o sta pensando di iniziare.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="mb-2 font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-ivory/40">Navigazione</span>
              <Link to="/" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-home">Home</Link>
              <Link to="/fai-una-domanda" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-ask">Consiglio gratuito</Link>
              <Link to="/chi-siamo" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-about">Chi siamo</Link>
            </div>
            <div className="flex flex-col gap-1">
              <span className="mb-2 font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-ivory/40">Legale &amp; contatti</span>
              <Link to="/privacy" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/cookie-policy" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-cookie">Cookie Policy</Link>
              <a href="mailto:admin@hairhelp.studio" className="min-h-[40px] py-1.5 font-manrope text-sm text-ivory/70 transition-colors hover:text-ivory" data-testid="footer-email">admin@hairhelp.studio</a>
            </div>
          </div>
          <div className="mt-12 border-t border-ivory/10 pt-6 text-center">
            <p className="font-editorial text-base italic text-ivory/70">Consigli veri, da persone a persone.</p>
            <p className="mt-2 font-manrope text-xs text-ivory/40">
              © {new Date().getFullYear()} HairHelp — hairhelp.studio. Consigli pratici basati sull&rsquo;esperienza, non pareri medici.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
